import { authClient, useSession } from '@/lib/auth-client';
import { getActiveTenant, isSubscriptionActive } from '@/lib/tenant';
import '@/styles/landing.css';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';

export const Route = createFileRoute('/auth/')({
    validateSearch: (search: Record<string, unknown>) => {
        const mode = search.mode === 'signup' ? 'signup' : undefined;
        return mode ? { mode } : {};
    },
    component: Login,
});

function Login() {
    const navigate = useNavigate();
    const { mode } = Route.useSearch();
    const { data: session, isPending } = useSession();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);
    const [needsConfirmation, setNeedsConfirmation] = useState(false);
    const isSignUp = mode === 'signup';

    useEffect(() => {
        if (!isPending && session) {
            navigate({ to: '/dashboard', replace: true });
        }
    }, [isPending, session, navigate]);

    useEffect(() => {
        setError(null);
        setInfo(null);
        setNeedsConfirmation(false);
    }, [isSignUp]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        setError(null);
        setInfo(null);
        setNeedsConfirmation(false);

        try {
            if (isSignUp) {
                const { session } = await authClient.signUp.email({
                    name,
                    email,
                    password,
                });
                if (!session) {
                    setNeedsConfirmation(true);
                    setInfo(
                        `We sent a confirmation link to ${email}. Please confirm your email to continue.`,
                    );
                    return;
                }
            } else {
                await authClient.signIn.email({ email, password });
            }
            const tenant = await getActiveTenant();
            navigate({
                to: isSubscriptionActive(tenant.subscriptionStatus)
                    ? '/dashboard'
                    : '/subscribe',
                replace: true,
            });
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Unable to authenticate.';
            setError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResend = async () => {
        if (!email) {
            setError('Enter your email address to resend the confirmation.');
            return;
        }

        setIsResending(true);
        setError(null);
        setInfo(null);

        try {
            await authClient.resendConfirmation(email);
            setInfo(`Confirmation email resent to ${email}.`);
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Unable to resend email.';
            setError(message);
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div
            className="sw-lp"
            style={{
                minHeight: '100vh',
                background: 'var(--lp-walnut-deep)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 20px',
                position: 'relative',
            }}
        >
            {/* subtle gradient accent, like the final CTA */}
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                        'radial-gradient(circle at 70% 20%, rgba(184,158,110,0.12), transparent 55%)',
                    pointerEvents: 'none',
                }}
            />

            {/* logo */}
            <Link to="/" style={{ marginBottom: 36, position: 'relative' }}>
                <img
                    src="https://res.cloudinary.com/flannery38/image/upload/v1771973120/stockwright/logo-white.svg"
                    alt="Stockwright"
                    style={{ height: 32, width: 'auto' }}
                />
            </Link>

            {/* card */}
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 440,
                    background: 'var(--lp-paper3)',
                    border: '1px solid var(--lp-hairline)',
                    boxShadow: '0 4px 32px rgba(0,0,0,0.22)',
                    padding: '40px 40px 36px',
                }}
            >
                {/* top accent bar */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: 'var(--lp-brass)',
                    }}
                />

                <div className="lp-eyebrow" style={{ marginBottom: 14 }}>
                    — {isSignUp ? 'Get started · Free trial' : 'Welcome back'}
                </div>

                <h2
                    style={{
                        fontFamily: 'Fraunces, Georgia, serif',
                        fontSize: 32,
                        fontWeight: 500,
                        lineHeight: 1.05,
                        letterSpacing: '-0.01em',
                        marginBottom: 32,
                        color: 'var(--lp-ink)',
                    }}
                >
                    {isSignUp ? (
                        <>
                            Start your{' '}
                            <em
                                style={{
                                    fontStyle: 'italic',
                                    fontWeight: 300,
                                    color: 'var(--lp-walnut)',
                                }}
                            >
                                free trial.
                            </em>
                        </>
                    ) : (
                        <>
                            Sign{' '}
                            <em
                                style={{
                                    fontStyle: 'italic',
                                    fontWeight: 300,
                                    color: 'var(--lp-walnut)',
                                }}
                            >
                                back in.
                            </em>
                        </>
                    )}
                </h2>

                <form
                    onSubmit={handleSubmit}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                    }}
                >
                    {isSignUp && (
                        <AuthField label="Name">
                            <AuthInput
                                id="name"
                                type="text"
                                autoComplete="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </AuthField>
                    )}

                    <AuthField label="Email">
                        <AuthInput
                            id="email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </AuthField>

                    <AuthField label="Password">
                        <AuthInput
                            id="password"
                            type="password"
                            autoComplete={
                                isSignUp ? 'new-password' : 'current-password'
                            }
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </AuthField>

                    {info && (
                        <div
                            style={{
                                border: '1px solid rgba(90,122,58,0.4)',
                                background: 'rgba(90,122,58,0.08)',
                                padding: '10px 14px',
                                borderRadius: 2,
                                fontSize: 13,
                                color: '#3d5a24',
                                lineHeight: 1.5,
                            }}
                        >
                            {info}
                        </div>
                    )}

                    {error && (
                        <div
                            style={{
                                border: '1px solid rgba(184,92,58,0.4)',
                                background: 'rgba(184,92,58,0.08)',
                                padding: '10px 14px',
                                borderRadius: 2,
                                fontSize: 13,
                                color: 'var(--lp-rust-deep)',
                                lineHeight: 1.5,
                            }}
                        >
                            {error}
                        </div>
                    )}

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                            marginTop: 8,
                        }}
                    >
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="lp-price-cta primary"
                        >
                            {isSubmitting
                                ? 'Working...'
                                : isSignUp
                                  ? 'Create account →'
                                  : 'Sign in →'}
                        </button>

                        {needsConfirmation && (
                            <button
                                type="button"
                                onClick={handleResend}
                                disabled={isResending}
                                className="lp-price-cta"
                            >
                                {isResending
                                    ? 'Resending...'
                                    : 'Resend confirmation email'}
                            </button>
                        )}

                        <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() =>
                                navigate({
                                    to: '/auth',
                                    search: (prev) => ({
                                        ...prev,
                                        mode: isSignUp ? undefined : 'signup',
                                    }),
                                })
                            }
                            style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontFamily: 'JetBrains Mono, monospace',
                                fontSize: 11,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: 'var(--lp-ink-mute)',
                                padding: '8px 0',
                                transition: 'color 0.2s',
                            }}
                            onMouseOver={(e) =>
                                ((e.target as HTMLElement).style.color =
                                    'var(--lp-walnut)')
                            }
                            onMouseOut={(e) =>
                                ((e.target as HTMLElement).style.color =
                                    'var(--lp-ink-mute)')
                            }
                        >
                            {isSignUp
                                ? 'Already have an account? Sign in'
                                : 'Need an account? Create one'}
                        </button>
                    </div>
                </form>
            </div>

            {/* footer note */}
            <div
                style={{
                    marginTop: 28,
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 10,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: 'rgba(247,242,230,0.35)',
                    position: 'relative',
                }}
            >
                14-day free trial
            </div>
        </div>
    );
}

function AuthField({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label
                style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 10,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: 'var(--lp-ink-mute)',
                    fontWeight: 500,
                }}
            >
                {label}
            </label>
            {children}
        </div>
    );
}

function AuthInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
    const [focused, setFocused] = useState(false);
    return (
        <input
            {...props}
            onFocus={(e) => {
                setFocused(true);
                props.onFocus?.(e);
            }}
            onBlur={(e) => {
                setFocused(false);
                props.onBlur?.(e);
            }}
            style={{
                width: '100%',
                border: `1px solid ${focused ? 'var(--lp-walnut)' : 'var(--lp-hairline)'}`,
                background: 'var(--lp-paper)',
                padding: '10px 12px',
                fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
                fontSize: 15,
                color: 'var(--lp-ink)',
                borderRadius: 2,
                outline: 'none',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box',
            }}
        />
    );
}
